import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { UserForm } from './UserForm';
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { UserRole } from '@/types/strategy-types';
import { useEmailTemplates } from '@/hooks/useEmailTemplates';

interface AddUserDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onUserAdded?: () => void;  // Add callback prop for refreshing users
}

export const AddUserDialog = ({ isOpen, onClose, onUserAdded }: AddUserDialogProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const { sendCredentialsEmail } = useEmailTemplates();

  const handleSubmit = async (data: {
    email: string;
    firstName: string;
    lastName: string;
    role: UserRole;
    password: string;
    sendEmail: boolean;
    forcePasswordChange: boolean;
  }) => {
    setIsSubmitting(true);
    setFormError(null);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("No active session");
      }

      const { data: userData, error } = await supabase.functions.invoke('create-user', {
        body: {
          email: data.email,
          password: data.password,
          firstName: data.firstName,
          lastName: data.lastName,
          role: data.role,
          forcePasswordChange: data.forcePasswordChange
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) throw error;
      
      if (userData && 'error' in userData) {
        if (userData.code === 'email_taken') {
          setFormError('This email address is already in use. Please use a different email.');
          setIsSubmitting(false);
          return;
        }
        
        throw new Error(userData.error || 'Failed to create user');
      }

      if (data.sendEmail) {
        try {
          await sendCredentialsEmail(
            data.email,
            data.password,
            data.firstName,
            data.forcePasswordChange
          );
        } catch (emailError) {
          console.error('Error sending credentials email:', emailError);
          toast.warning('User created successfully, but failed to send email with credentials.');
        }
      }

      if (data.role !== 'admin') {
        try {
          await supabase.functions.invoke('klaviyo-subscribe', {
            body: { 
              userId: userData.id || "unknown", 
              email: data.email, 
              firstName: data.firstName, 
              lastName: data.lastName 
            },
            headers: {
              Authorization: `Bearer ${session.access_token}`
            }
          });
          console.log("New user subscribed to Klaviyo newsletter");
        } catch (klaviyoError) {
          console.error('Error subscribing user to Klaviyo:', klaviyoError);
        }
      }

      toast.success('User created successfully');
      onClose();
      
      if (onUserAdded) {
        onUserAdded();
      }
      
    } catch (error) {
      console.error('Error creating user:', error);
      
      if (error.json && typeof error.json === 'function') {
        try {
          const errorData = await error.json();
          if (errorData.code === 'email_taken') {
            setFormError('This email address is already in use. Please use a different email.');
          } else {
            setFormError(errorData.error || 'Failed to create user');
          }
        } catch (jsonError) {
          setFormError(error.message || 'Failed to create user');
        }
      } else {
        setFormError(error.message || 'Failed to create user');
      }
      
      toast.error('Failed to create user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormError(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Add New User</DialogTitle>
        </DialogHeader>
        
        {formError && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        )}
        
        <UserForm 
          onSubmit={handleSubmit} 
          isSubmitting={isSubmitting} 
          error={formError}
        />
      </DialogContent>
    </Dialog>
  );
};
